/**
 * Workflow Simulator - Test workflow selection and execution
 */

import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { 
  Play, 
  RefreshCw, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Users, 
  GitBranch, 
  AlertTriangle,
  UserCheck,
  ChevronRight,
  Search,
  Settings,
  Zap,
  Target,
} from "lucide-react";

// Process types for simulation
const PROCESS_TYPES = [
  { value: "admin_visit", label: "Admin Visit" },
  { value: "work_permit", label: "Work Permit" },
  { value: "material_entry", label: "Material Entry" },
  { value: "tep", label: "TEP (Temporary Entry Pass)" },
  { value: "mop", label: "MOP (Method of Procedure)" },
  { value: "escort", label: "Escort Request" },
];

// Sample request data
const SAMPLE_REQUESTS = [
  {
    id: 1,
    processType: "admin_visit",
    requesterName: "John Doe",
    siteName: "Main Campus",
    zoneName: "Building A",
    areaName: "Floor 3",
    visitorCount: 2,
    duration: 4,
    isVip: false,
    riskLevel: "low",
  },
  {
    id: 2,
    processType: "work_permit",
    requesterName: "Jane Smith",
    siteName: "Industrial Site",
    zoneName: "Production Area",
    areaName: "Line 2",
    visitorCount: 5,
    duration: 8,
    isVip: true,
    riskLevel: "high",
  },
  {
    id: 3,
    processType: "tep",
    requesterName: "Mike Johnson",
    siteName: "Office Complex",
    zoneName: "IT Department",
    areaName: "Server Room",
    visitorCount: 1,
    duration: 2,
    isVip: false,
    riskLevel: "medium",
  },
];

interface WorkflowSimulatorProps {
  isOpen: boolean;
  onClose: () => void;
}

export function WorkflowSimulator({ isOpen, onClose }: WorkflowSimulatorProps) {
  const [selectedRequest, setSelectedRequest] = useState<any>(null);
  const [customRequest, setCustomRequest] = useState({
    processType: "",
    siteId: "",
    zoneId: "",
    areaId: "",
    visitorCount: 1,
    duration: 1,
    isVip: false,
    riskLevel: "low",
  });
  const [simulationResult, setSimulationResult] = useState<any>(null);
  const [isSimulating, setIsSimulating] = useState(false);
  const [expandedStages, setExpandedStages] = useState<Set<number>>(new Set());
  const utils = trpc.useUtils();

  // Queries
  const { data: workflows } = trpc.workflows.list.useQuery();
  const { data: sites } = trpc.sites.getAll.useQuery();
  const { data: zones } = trpc.zones.getAll.useQuery();
  const { data: areas } = trpc.areas.getAll.useQuery();

  // Simulate workflow query
  const simulateWorkflow = (trpc.workflows.simulate as any).useQuery(
    { processType: 'test' },
    { enabled: false }
  );

  const handleSimulate = () => {
    setIsSimulating(true);
    simulateWorkflow.refetch().then((result: any) => {
      if (result.data) {
        setSimulationResult(result.data);
        toast.success("Workflow simulation completed");
      }
    }).catch((error: any) => {
      toast.error(error?.message || "Simulation failed");
    }).finally(() => {
      setIsSimulating(false);
    });
  };

  (simulateWorkflow as any).mutate = handleSimulate;

  const buildSimulationInput = () => {
    const requestData = selectedRequest || customRequest;
    return {
      processType: requestData.processType,
      siteId: requestData.siteId ? Number(requestData.siteId) : undefined,
      zoneId: requestData.zoneId ? Number(requestData.zoneId) : undefined,
      areaId: requestData.areaId ? Number(requestData.areaId) : undefined,
      visitorCount: requestData.visitorCount ? Number(requestData.visitorCount) : undefined,
      duration: requestData.duration ? Number(requestData.duration) : undefined,
      isVip: requestData.isVip,
      riskLevel: requestData.riskLevel || undefined,
    };
  };

  const availableZones = customRequest.siteId
    ? (zones || []).filter((zone: any) => String(zone.siteId) === String(customRequest.siteId))
    : zones || [];

  const availableAreas = customRequest.zoneId
    ? (areas || []).filter((area: any) => String(area.zoneId) === String(customRequest.zoneId))
    : areas || [];

  const handleRunSimulation = async () => {
    const requestData = buildSimulationInput();
    
    if (!requestData.processType) {
      toast.error("Please select a process type");
      return;
    }

    setIsSimulating(true);
    utils.workflows.simulate.fetch(requestData).then((result) => {
      setSimulationResult(result);
      setIsSimulating(false);
    }).catch((error) => {
      console.error("Simulation error:", error);
      toast.error("Failed to simulate workflow");
      setIsSimulating(false);
    });
  };

  const handleSelectSampleRequest = (request: any) => {
    setSelectedRequest(request);
    setCustomRequest({
      processType: "",
      siteId: "",
      zoneId: "",
      areaId: "",
      visitorCount: 1,
      duration: 1,
      isVip: false,
      riskLevel: "low",
    });
    setSimulationResult(null);
  };

  const handleReset = () => {
    setSelectedRequest(null);
    setCustomRequest({
      processType: "",
      siteId: "",
      zoneId: "",
      areaId: "",
      visitorCount: 1,
      duration: 1,
      isVip: false,
      riskLevel: "low",
    });
    setSimulationResult(null);
  };

  const toggleStageExpanded = (stageId: number) => {
    const newExpanded = new Set(expandedStages);
    if (newExpanded.has(stageId)) {
      newExpanded.delete(stageId);
    } else {
      newExpanded.add(stageId);
    }
    setExpandedStages(newExpanded);
  };

  const renderWorkflowPath = (workflow: any, stages: any[]) => {
    if (!workflow) {
      return (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-900">
          No workflow matched the provided request data.
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Badge variant="default" className="bg-[#5B2C93]">
            {workflow.name}
          </Badge>
          <span className="text-sm text-[#6B6B6B]">
            Priority: {workflow.priority}
          </span>
          {workflow.isDefault && (
            <Badge variant="secondary">Default</Badge>
          )}
        </div>

        <div className="space-y-3">
          {stages.map((stage, index) => {
            const isExpanded = expandedStages.has(stage.id);
            return (
            <div key={stage.id} className="border rounded-lg p-3 hover:bg-[#F5F5F5]/50 transition-colors">
              <div className="flex items-start gap-3 cursor-pointer" onClick={() => toggleStageExpanded(stage.id)}>
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-[#5B2C93] text-white font-medium text-sm flex-shrink-0">
                  {index + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="font-medium">{stage.stageName}</h4>
                    <Badge variant="outline" className="text-xs">
                      {stage.approvalMode}
                    </Badge>
                    {stage.slaHours && (
                      <Badge variant="secondary" className="text-xs">
                        <Clock className="h-3 w-3 mr-1" />
                        {stage.slaHours}h
                      </Badge>
                    )}
                  </div>
                  
                  {/* Brief Approvers Summary */}
                  {stage.approvers && stage.approvers.length > 0 && !isExpanded && (
                    <div className="mt-1 text-xs text-[#6B6B6B]">
                      {stage.approvers.length} approver{stage.approvers.length !== 1 ? 's' : ''}
                    </div>
                  )}
                </div>
                
                <ChevronRight className={`h-5 w-5 text-[#6B6B6B] transition-transform flex-shrink-0 ${isExpanded ? 'rotate-90' : ''}`} />
              </div>
              
              {/* Expanded Details */}
              {isExpanded && (
                <div className="mt-3 ml-11 space-y-2 border-t pt-3">
                  {/* Approvers */}
                  {stage.approvers && stage.approvers.length > 0 && (
                    <div className="space-y-1">
                      <h5 className="text-xs font-semibold text-[#6B6B6B] uppercase">Approvers ({stage.approvers.length})</h5>
                      {stage.approvers.map((approver: any, approverIndex: number) => {
                        // Format approver display
                        let approverLabel = '';
                        if (approver.approverType === 'user' && approver.userId) {
                          approverLabel = `User: ${approver.userId}`;
                        } else if (approver.approverType === 'role' && approver.roleId) {
                          approverLabel = `Role: ${approver.roleId}`;
                        } else if (approver.approverType === 'group' && approver.groupId) {
                          approverLabel = `Group: ${approver.groupId}`;
                        } else if (approver.approverType === 'manager_chain') {
                          approverLabel = 'Manager Chain';
                        } else if (approver.approverType === 'hierarchy_level') {
                          approverLabel = `Hierarchy Level: ${approver.hierarchyLevel || 'N/A'}`;
                        } else {
                          approverLabel = approver.summary || approver.approverType || 'Unknown Approver';
                        }
                        
                        return (
                          <div key={approverIndex} className="flex items-center gap-2 text-sm text-[#6B6B6B]">
                            <UserCheck className="h-3 w-3" />
                            <span>{approverLabel}</span>
                            {approver.weight && approver.weight > 1 && (
                              <Badge variant="secondary" className="text-xs px-1 py-0">
                                w:{approver.weight}
                              </Badge>
                            )}
                            {approver.required && (
                              <Badge variant="default" className="text-xs px-1 py-0 bg-red-500">
                                Required
                              </Badge>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Escalation Rules */}
                  {stage.escalationRules && stage.escalationRules.length > 0 && (
                    <div className="space-y-1">
                      <h5 className="text-xs font-semibold text-[#6B6B6B] uppercase">Escalation Rules</h5>
                      {stage.escalationRules.map((rule: any, ruleIndex: number) => (
                        <div key={ruleIndex} className="flex items-center gap-2 text-sm text-[#6B6B6B]">
                          <AlertTriangle className="h-3 w-3" />
                          <span>{rule.triggerType} after {rule.afterHours}h</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Workflow Simulator</h2>
          <p className="text-[#6B6B6B]">Test workflow selection and approval flow</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleReset}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Reset
          </Button>
          <Button 
            onClick={handleRunSimulation} 
            disabled={isSimulating}
            className="bg-[#5B2C93] hover:bg-[#3D1C5E]"
          >
            <Play className="h-4 w-4 mr-2" />
            {isSimulating ? "Simulating..." : "Run Simulation"}
          </Button>
        </div>
      </div>

      <Tabs defaultValue="sample" className="w-full">
        <TabsList>
          <TabsTrigger value="sample">Sample Requests</TabsTrigger>
          <TabsTrigger value="custom">Custom Request</TabsTrigger>
          <TabsTrigger value="results">Results</TabsTrigger>
        </TabsList>

        {/* Sample Requests Tab */}
        <TabsContent value="sample" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Select Sample Request</CardTitle>
              <CardDescription>
                Choose a pre-configured request to test workflow selection
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3">
                {SAMPLE_REQUESTS.map((request) => (
                  <Card
                    key={request.id}
                    className={`cursor-pointer transition-colors ${
                      selectedRequest?.id === request.id
                        ? "border-primary bg-[#5B2C93]/5"
                        : "hover:bg-[#F5F5F5]/50"
                    }`}
                    onClick={() => handleSelectSampleRequest(request)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge variant="outline">
                              {PROCESS_TYPES.find(pt => pt.value === request.processType)?.label}
                            </Badge>
                            <span className="font-medium">{request.requesterName}</span>
                            {request.isVip && (
                              <Badge variant="default" className="bg-yellow-500">VIP</Badge>
                            )}
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-sm text-[#6B6B6B]">
                            <span>📍 {request.siteName} → {request.zoneName}</span>
                            <span>👥 {request.visitorCount} visitors</span>
                            <span>⏱️ {request.duration} hours</span>
                            <span>⚠️ {request.riskLevel} risk</span>
                          </div>
                        </div>
                        {selectedRequest?.id === request.id && (
                          <CheckCircle className="h-5 w-5 text-green-500" />
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Custom Request Tab */}
        <TabsContent value="custom" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Create Custom Request</CardTitle>
              <CardDescription>
                Build a custom request to test specific scenarios
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Process Type *</Label>
                  <Select
                    value={customRequest.processType}
                    onValueChange={(value) => setCustomRequest({ ...customRequest, processType: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select process type" />
                    </SelectTrigger>
                    <SelectContent>
                      {PROCESS_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Site</Label>
                  <Select
                    value={customRequest.siteId}
                    onValueChange={(value) => setCustomRequest({ ...customRequest, siteId: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select site" />
                    </SelectTrigger>
                    <SelectContent>
                      {sites?.map((site: any) => (
                        <SelectItem key={site.id} value={String(site.id)}>
                          {site.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Zone</Label>
                  <Select
                    value={customRequest.zoneId}
                    onValueChange={(value) => setCustomRequest({ ...customRequest, zoneId: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select zone" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableZones.map((zone: any) => (
                        <SelectItem key={zone.id} value={String(zone.id)}>
                          {zone.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Area</Label>
                  <Select
                    value={customRequest.areaId}
                    onValueChange={(value) => setCustomRequest({ ...customRequest, areaId: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select area" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableAreas.map((area: any) => (
                        <SelectItem key={area.id} value={String(area.id)}>
                          {area.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Visitor Count</Label>
                  <Input
                    type="number"
                    min={1}
                    value={customRequest.visitorCount}
                    onChange={(e) => setCustomRequest({ 
                      ...customRequest, 
                      visitorCount: parseInt(e.target.value) || 1 
                    })}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Duration (hours)</Label>
                  <Input
                    type="number"
                    min={1}
                    value={customRequest.duration}
                    onChange={(e) => setCustomRequest({ 
                      ...customRequest, 
                      duration: parseInt(e.target.value) || 1 
                    })}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Risk Level</Label>
                  <Select
                    value={customRequest.riskLevel}
                    onValueChange={(value) => setCustomRequest({ ...customRequest, riskLevel: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="critical">Critical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center space-x-2 pt-6">
                  <input
                    type="checkbox"
                    id="isVip"
                    checked={customRequest.isVip}
                    onChange={(e) => setCustomRequest({ ...customRequest, isVip: e.target.checked })}
                  />
                  <Label htmlFor="isVip">VIP Visit</Label>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Results Tab */}
        <TabsContent value="results" className="space-y-4">
          {simulationResult ? (
            <div className="space-y-4">
              {/* Selected Workflow */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Target className="h-5 w-5 text-[#5B2C93]" />
                    Selected Workflow
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {renderWorkflowPath(simulationResult.workflow, simulationResult.stages)}
                </CardContent>
              </Card>



              {/* Matching Conditions */}
              {simulationResult.matchedConditions && simulationResult.matchedConditions.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Zap className="h-5 w-5 text-yellow-500" />
                      Matched Conditions
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {simulationResult.matchedConditions.map((condition: any, index: number) => (
                        <div key={index} className="flex items-center gap-2 text-sm">
                          <CheckCircle className="h-4 w-4 text-green-500" />
                          <span>{condition.description}</span>
                          <Badge variant="outline">Group {condition.logicalGroup ?? 0}</Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {simulationResult.error && (
                <Card>
                  <CardContent className="py-4 text-sm text-red-700">
                    {simulationResult.error}
                  </CardContent>
                </Card>
              )}

              {/* Execution Timeline */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Clock className="h-5 w-5 text-blue-500" />
                    Expected Timeline
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {simulationResult.timeline?.map((item: any, index: number) => (
                      <div key={index} className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${
                          item.status === 'completed' ? 'bg-green-500' :
                          item.status === 'pending' ? 'bg-yellow-500' :
                          'bg-gray-300'
                        }`} />
                        <div className="flex-1">
                          <div className="font-medium">{item.stage}</div>
                          <div className="text-sm text-[#6B6B6B]">{item.description}</div>
                        </div>
                        <div className="text-sm text-[#6B6B6B]">{item.duration}</div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : (
            <Card>
              <CardContent className="py-12 text-center text-[#6B6B6B]">
                <Settings className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No simulation results yet</p>
                <p className="text-sm">Select a request and run simulation to see results</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
